import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as userApi from '@/apis/users';
import * as authApi from '@/apis/auth';
import { authAtom } from '@/atom/authAtom';
import { useAtom } from 'jotai';

// 쿼리 키 상수화 (오타 방지 및 중앙 관리)
export const USER_KEYS = {
  all: ['users'],
  lists: () => [...USER_KEYS.all, 'list'],
  list: (params) => [...USER_KEYS.lists(), params],
  details: () => [...USER_KEYS.all, 'detail'],
  detail: (id) => [...USER_KEYS.details(), id],
  me: ['myInfo'],
};

/* ==========================================
  Queries (GET)
========================================== */

// 1. 회원 목록 조회 (Pagination)
export const useUserList = (params) => {
  return useQuery({
    queryKey: USER_KEYS.list(params), // params가 바뀌면 자동 재조회
    queryFn: () => userApi.fetchUserList(params),
  });
};

// 2. 회원 상세 조회
export const useUserDetail = (userId) => {
  return useQuery({
    queryKey: USER_KEYS.detail(userId),
    queryFn: () => userApi.fetchUserDetail(userId),
    enabled: !!userId, // userId가 있을 때만 실행
  });
};

/* ==========================================
  Mutations (POST, PUT, PATCH, DELETE)
========================================== */

// 1. 회원 등록 (단건)
export const useJoinUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userApi.joinUser,
    onSuccess: () => {
      // 목록 새로고침
      queryClient.invalidateQueries({ queryKey: USER_KEYS.lists() });
    },
  });
};

// 2. 회원 대량 등록 (Excel)
export const useJoinUsersBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, file, sendWelcomeEmail }) => userApi.joinUsersBulk(groupId, file, sendWelcomeEmail),
    onSuccess: (_data, variables) => {
      const groupId = Number(variables.groupId);
      queryClient.invalidateQueries({
        queryKey: USER_KEYS.list({
          page: 1,
          size: 10,
          groupId,
          isStudent: true,
          type: 'all',
          search: '',
        }),
      });
      // 같은 groupId인데 page·search·type이 다른 캐시(예: 2페이지, 검색 중)도 갱신
      queryClient.invalidateQueries({
        queryKey: USER_KEYS.lists(),
        predicate: (query) => {
          const params = query.queryKey[2];
          return params != null && typeof params === 'object' && Number(params.groupId) === groupId;
        },
      });
    },
  });
};

// 3. 회원 정보 수정
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userApi.updateUser,
    onSuccess: (data, variables) => {
      // 목록 갱신
      queryClient.invalidateQueries({ queryKey: USER_KEYS.lists() });
      // 상세 정보 갱신 (수정된 유저의 상세 정보가 열려있을 수 있으므로)
      queryClient.invalidateQueries({ queryKey: USER_KEYS.detail(variables.userId) });
    },
  });
};

// 4. 회원 상태 변경 (Status 토글)
export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, status, secessionOpinion }) =>
      userApi.updateUserStatus(userId, status, secessionOpinion),
    onSuccess: (data, variables) => {
      // 목록만 갱신해도 충분 (보통 리스트에서 토글하니까)
      queryClient.invalidateQueries({ queryKey: USER_KEYS.lists() });
      // 필요하다면 상세도 갱신
      queryClient.invalidateQueries({ queryKey: USER_KEYS.detail(variables.userId) });
    },
  });
};

// 5. 회원 삭제
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_KEYS.lists() });
    },
  });
};

// 6. ID 중복 체크 (단순 체크라 Mutation 사용이 적합)
export const useCheckUserId = () => {
  return useMutation({
    mutationFn: userApi.checkUserIdDuplicate,
    // onSuccess 로직은 컴포넌트에서 처리 (예: "사용 가능한 ID입니다" 메시지)
  });
};